/**
 * Data Transformation Utilities
 * Functions to transform data between different component formats
 */

// Compsheet field name to valuation dbField mapping
const COMPSHEET_TO_VALUATION_X = {
  'Revenue YOY%': 'revenueyoy',
  'Gross Margin YOY%': 'grossMarginyoy',
  'TTM Billings YOY%': 'currentCalculatedBillingsttmYoy',
  'TTM Current Bookings YOY%': 'currentCalculatedBookingsttmYoy',
  'TTM Bookings YOY%': 'calculatedBookingsttmYoy',
  'Billings YOY%': 'currentCalculatedBillingsyoy',
  'Current Bookings YOY%': 'currentCalculatedBookingsyoy',
  'Bookings YOY%': 'calculatedBookingsyoy',
  'CRPO YOY%': 'currentRpoyoy',
  'RPO YOY%': 'rpoyoy',
  'Rule of 40': 'ruleOf40values',
  'Rule of X': 'ruleOfXvalues',
  'Rule of X Bookings': 'ruleOfXBookingsvalues',
  'ARR YOY%': 'arryoy',
  'Customers YOY%': 'customersyoy',
  'Deferred Revenue YOY%': 'deferredRevenueyoy',
};

const COMPSHEET_TO_VALUATION_Y = {
  'EV/Sales NTM': 'evToSalesNTM',
  'EV/GM NTM': 'evToGrossMarginNTM',
  'EV/Sales TTM': 'evToSalesTTM',
  'EV/GM TTM': 'evToGrossMarginTTM',
  'Price/FCF TTM': 'priceToFcfTTM',
};

/**
 * Transform flat compsheet data to scatter plot (valuation) format
 *
 * @param {Array} compsheetData - Array of flat compsheet objects
 * @returns {Array} Array of transformed objects with nested valuesX/valuesY structure
 *
 * Input format (compsheet.json):
 * {
 *   "Company Symbol": "PLTR",
 *   "Company Name": "Palantir",
 *   "Market": "AI Tools, IaaS, APIs & Low Code",
 *   "Revenue YOY%": 0.36,
 *   "EV/Sales NTM": 57.5
 * }
 *
 * Output format (valuation.json structure):
 * {
 *   ticker: "PLTR",
 *   companyName: "Palantir",
 *   category: "AI Tools, IaaS, APIs & Low Code",
 *   valuesX: [{
 *     revenueyoy: 0.36,
 *     grossMarginyoy: ...,
 *     ...
 *   }],
 *   valuesY: [{
 *     evToSalesNTM: 57.5,
 *     evToGrossMarginNTM: ...,
 *   }]
 * }
 */
export const transformCompsheetToScatter = (compsheetData) => {
  if (!Array.isArray(compsheetData)) return [];

  return compsheetData.map(row => {
    // Build valuesX object from X-axis metrics
    const valuesX = {};
    Object.entries(COMPSHEET_TO_VALUATION_X).forEach(([compsheetField, valuationDbField]) => {
      const value = row[compsheetField];
      if (value !== undefined && value !== null && !isNaN(value)) {
        valuesX[valuationDbField] = value;
      }
    });

    // Build valuesY object from Y-axis metrics (valuation multiples)
    const valuesY = {};
    Object.entries(COMPSHEET_TO_VALUATION_Y).forEach(([compsheetField, valuationDbField]) => {
      const value = row[compsheetField];
      if (value !== undefined && value !== null && !isNaN(value)) {
        valuesY[valuationDbField] = value;
      }
    });

    return {
      ticker: row['Company Symbol'],
      companyName: row['Company Name'],
      category: row['Market'],
      valuesX: [valuesX],
      valuesY: [valuesY],
    };
  }).filter(row => row.ticker && row.companyName);
};

/**
 * Transform compsheet data for Dashboard bar chart
 * Dashboard expects flat data with specific field names
 *
 * @param {Array} compsheetData - Array of flat compsheet objects
 * @returns {Array} Same format, just passed through (compsheet format matches dashboard needs)
 */
export const transformCompsheetToDashboard = (compsheetData) => {
  // Dashboard already uses compsheet format directly
  // This function exists for API consistency
  return compsheetData;
};

/**
 * Get available X-axis metrics from transformed scatter data
 * @param {Array} scatterData - Transformed scatter data
 * @returns {Set} Set of available dbField names
 */
export const getAvailableXMetrics = (scatterData) => {
  const available = new Set();
  if (!Array.isArray(scatterData)) return available;

  scatterData.forEach(row => {
    if (row.valuesX?.[0]) {
      Object.keys(row.valuesX[0]).forEach(key => available.add(key));
    }
  });

  return available;
};

/**
 * Get available Y-axis metrics from transformed scatter data
 * @param {Array} scatterData - Transformed scatter data
 * @returns {Set} Set of available dbField names
 */
export const getAvailableYMetrics = (scatterData) => {
  const available = new Set();
  if (!Array.isArray(scatterData)) return available;

  scatterData.forEach(row => {
    if (row.valuesY?.[0]) {
      Object.keys(row.valuesY[0]).forEach(key => available.add(key));
    }
  });

  return available;
};
